import { Input } from '@api/Inputs/Input';
import { ShrinkageReason } from '@api/Inventory/InventoryMovement';

// ============================================================================
// MERMA, lo que la PANTALLA decide antes de llamar al server (plan de inventario F2).
// La regla que manda vive en el server (`InventoryShrinkage.ts`: signo, valuación,
// motivo, nota, autorizador) y responde 400 con el motivo; aquí solo lo que hace
// falta para no mandar basura y para informar mientras se captura.
// ============================================================================

/** Un renglón del modal: el insumo elegido y la cantidad como texto (campo numérico). */
export interface ShrinkageLine {
    inputId: string;
    quantity: string;
}

/** Lo que se manda al server: solo los renglones con insumo y cantidad > 0, acumulados. */
export const buildShrinkageEntries = (lines: ShrinkageLine[]): { inputId: string; quantity: number }[] => {
    const byInput = new Map<string, number>();
    (lines || []).forEach((line) => {
        const quantity = Number(line.quantity);
        if (!line.inputId || !Number.isFinite(quantity) || quantity <= 0) return;
        byInput.set(line.inputId, (byInput.get(line.inputId) || 0) + quantity);
    });
    return Array.from(byInput.entries()).map(([inputId, quantity]) => ({ inputId, quantity }));
};

/** Con "Otro" la nota es obligatoria (sin ella el motivo no dice nada). */
export const isShrinkageNoteRequired = (reason?: ShrinkageReason): boolean => reason === 'other';

/** El primer problema que impide guardar, dicho para quien captura; `null` = se puede. */
export const validateShrinkageDraft = (
    lines: ShrinkageLine[],
    reason: ShrinkageReason | undefined,
    note: string,
    authorizedBy?: string
): string | null => {
    if (!reason) return 'Elige el motivo de la merma';
    if (isShrinkageNoteRequired(reason) && !note.trim()) return 'Con "Otro" escribe qué pasó';
    if (buildShrinkageEntries(lines).length === 0) return 'Agrega al menos un insumo con cantidad mayor a cero';
    if (!authorizedBy) return 'Falta el PIN de quien autoriza';
    return null;
};

/** $ por unidad base de un insumo: lo que persiste `saveInput`, o derivado igual. */
export const getUnitCostSale = (input?: Pick<Input, 'unitCostSale' | 'costOfSale' | 'unit'>): number => {
    const persisted = Number(input?.unitCostSale);
    if (persisted > 0) return persisted;
    const value = Number(input?.unit?.value);
    return value > 0 ? (Number(input?.costOfSale) || 0) / value : 0;
};

/** Lo que vale la merma, aproximado (el server valúa de verdad): Σ cantidad × costo por unidad base. */
export const estimateShrinkageAmount = (lines: ShrinkageLine[], inputsById: Map<string, Input>): number =>
    Math.round(
        buildShrinkageEntries(lines).reduce(
            (total, entry) => total + entry.quantity * getUnitCostSale(inputsById.get(entry.inputId)),
            0
        ) * 100
    ) / 100;
